import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDown, Coins } from 'lucide-react';
import { useLang } from '../i18n';
import { useCurrency } from '../utils/currency';

/** Compact header currency selector. Rates come only from the backend (provider/manual);
 * when unavailable the site keeps showing original EUR prices. */
export const CurrencySelector = ({ dark = true, compact = false, testidPrefix = 'nav-currency' }) => {
  const { t } = useLang();
  const { currency, setCurrency, currencies, ratesMeta, ratesFailed } = useCurrency();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const close = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        data-testid={`${testidPrefix}-button`}
        aria-label={t('cur.label')}
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        className={`flex items-center gap-1 text-sm font-medium transition-colors hover:text-eco-gold ${dark ? 'text-eco-ink/80' : 'text-white/85'}`}
      >
        {!compact && <Coins size={14} className="opacity-70" />}
        {currency} <ChevronDown size={13} className="opacity-60" />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 6 }}
            transition={{ duration: 0.2 }}
            data-testid={`${testidPrefix}-menu`}
            className="absolute right-0 z-50 mt-3 w-64 overflow-hidden rounded-xl border border-eco-ink/10 bg-white shadow-xl"
          >
            {currencies.map((c) => (
              <button
                key={c.code}
                type="button"
                data-testid={`${testidPrefix}-${c.code.toLowerCase()}`}
                onClick={() => { setCurrency(c.code); setOpen(false); }}
                className={`block w-full px-4 py-2.5 text-left text-sm transition-colors hover:bg-eco-cream ${currency === c.code ? 'font-semibold text-eco-green' : 'text-eco-ink/70'}`}
              >
                {c.code} — {t(c.nameKey)}
              </button>
            ))}
            <p data-testid={`${testidPrefix}-rates-meta`} className="border-t border-eco-ink/10 px-4 py-2.5 text-[10px] leading-relaxed text-eco-ink/45">
              {ratesFailed && t('cur.unavailable')}
              {!ratesFailed && ratesMeta && (
                <>
                  {ratesMeta.source === 'manual' ? t('cur.manual') : t('cur.updated', { date: ratesMeta.lastUpdate ? new Date(ratesMeta.lastUpdate).toLocaleDateString() : '—' })}
                  {ratesMeta.stale && ` · ${t('cur.stale')}`}
                  {ratesMeta.attribution && <span className="block">{t('cur.attribution')} {ratesMeta.attribution}</span>}
                </>
              )}
              {!ratesFailed && !ratesMeta && t('cur.loading')}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
